import { useLayoutEffect, useState } from "react";
import { ResizeObserver } from "../utils/ResizeObserver";
let singleton;
const observeHandlers = new WeakMap();
const getSingleton = () => {
    singleton =
        singleton ||
            new ResizeObserver((entries) => {
                entries.forEach((entry) => {
                    const handler = observeHandlers.get(entry.target);
                    handler === null || handler === void 0 ? void 0 : handler(entry.contentRect);
                });
            });
    return singleton;
};
const getElementRect = (rect) => ({
    x: rect.x,
    y: rect.y,
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
    height: rect.height,
    width: rect.width,
});
const getElementScrollAndClientSizes = (element) => ({
    scrollHeight: element.scrollHeight,
    clientHeight: element.clientHeight,
    scrollWidth: element.scrollWidth,
    clientWidth: element.clientWidth,
});
/**
 * Observe the resize of an element.
 * Internally, it uses `ResizeObserver` and `requestAnimationFrame`.
 * Inspired by `@restart/hooks/useResizeObserver`, fixed to avoid ResizeObserver loops.
 *
 * ```ts
 * const [ref, setRef] = useState<HTMLDivElement | null>(null);
 * const contentRect = useResizeObserver(ref);
 *
 * return (
 *  <div ref={setRef}>
 *    Current width is {contentRect.width}px.
 *  </div>
 * )
 * ```
 */
const useResizeObserver = (element, onResize) => {
    const [contentRect, setContentRect] = useState();
    useLayoutEffect(() => {
        if (!element) {
            return;
        }
        getSingleton().observe(element);
        let animationFrameId = null;
        observeHandlers.set(element, (newContentRect) => {
            animationFrameId = window.requestAnimationFrame(() => {
                setContentRect(Object.assign(Object.assign({}, getElementScrollAndClientSizes(element)), getElementRect(newContentRect)));
                onResize === null || onResize === void 0 ? void 0 : onResize();
            });
        });
        // initial dimensions
        setContentRect(Object.assign(Object.assign({}, getElementScrollAndClientSizes(element)), getElementRect(element.getBoundingClientRect())));
        return () => {
            animationFrameId && window.cancelAnimationFrame(animationFrameId);
            getSingleton().unobserve(element);
            observeHandlers.delete(element);
        };
    }, [element, onResize]);
    return contentRect;
};
export default useResizeObserver;
//# sourceMappingURL=useResizeObserver.js.map